// src/pages/finance-portal/bills/BillForm.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Autocomplete,
    MenuItem, Select, FormControl, InputLabel, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_TERMS = [
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_60', label: 'Net 60' },
    { value: 'NET_90', label: 'Net 90' },
];

const TERM_DAYS = {
    DUE_ON_RECEIPT: 0,
    NET_15: 15,
    NET_30: 30,
    NET_60: 60,
    NET_90: 90,
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function addDays(dateStr, days) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
}

const emptyLine = {
    description: '',
    gl_account_id: null,
    gl_account: null,
    quantity: 1,
    unit_price: 0,
    tax_amount: 0,
};

export default function BillForm() {
    const navigate = useNavigate();
    const { billId } = useParams();
    const isEdit = Boolean(billId);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Vendor
    const [vendorOptions, setVendorOptions] = useState([]);
    const [vendorSearch, setVendorSearch] = useState('');
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [vendorLoading, setVendorLoading] = useState(false);
    const vendorTimer = useRef(null);

    // GL Accounts
    const [glAccounts, setGlAccounts] = useState([]);
    const [glLoading, setGlLoading] = useState(false);

    // Bill fields
    const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState('');
    const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDateManual, setDueDateManual] = useState(false);
    const [paymentTerms, setPaymentTerms] = useState('NET_30');
    const [memo, setMemo] = useState('');
    const [referenceNumber, setReferenceNumber] = useState('');

    // Line items
    const [lines, setLines] = useState([{ ...emptyLine }]);

    // Existing bill data (for edit)
    const [existingBill, setExistingBill] = useState(null);

    // Fetch GL accounts on mount
    useEffect(() => {
        setGlLoading(true);
        axiosInstance.get('/api/finance/coa', { params: { flat: true } })
            .then(res => {
                if (res.data.success) setGlAccounts(res.data.data || []);
            })
            .catch(() => {})
            .finally(() => setGlLoading(false));
    }, []);

    // Auto-calculate due date from payment terms
    useEffect(() => {
        if (!dueDateManual && billDate && paymentTerms) {
            const days = TERM_DAYS[paymentTerms] || 0;
            setDueDate(addDays(billDate, days));
        }
    }, [billDate, paymentTerms, dueDateManual]);

    // Vendor search with debounce
    useEffect(() => {
        if (vendorTimer.current) clearTimeout(vendorTimer.current);
        const params = { per_page: 50 };
        if (vendorSearch && vendorSearch.length >= 2) params.search = vendorSearch;
        setVendorLoading(true);
        vendorTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/finance/vendors', { params });
                if (res.data.success) {
                    setVendorOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search vendors', err);
            } finally {
                setVendorLoading(false);
            }
        }, vendorSearch ? 300 : 0);
        return () => { if (vendorTimer.current) clearTimeout(vendorTimer.current); };
    }, [vendorSearch]);

    // Load existing bill for edit
    useEffect(() => {
        if (isEdit) {
            setLoading(true);
            axiosInstance.get(`/api/finance/bills/${billId}`)
                .then(res => {
                    if (res.data.success) {
                        const bill = res.data.data;
                        setExistingBill(bill);
                        setVendorInvoiceNumber(bill.vendor_invoice_number || '');
                        setBillDate(bill.bill_date || '');
                        setDueDate(bill.due_date || '');
                        setDueDateManual(true);
                        setPaymentTerms(bill.payment_terms || 'NET_30');
                        setMemo(bill.memo || '');
                        setReferenceNumber(bill.reference_number || '');

                        if (bill.vendor) {
                            setSelectedVendor(bill.vendor);
                            setVendorOptions([bill.vendor]);
                        }

                        if (bill.line_items && bill.line_items.length > 0) {
                            setLines(bill.line_items.map(li => ({
                                id: li.id,
                                description: li.description || '',
                                gl_account_id: li.gl_account_id || null,
                                gl_account: li.gl_account || null,
                                quantity: li.quantity != null ? li.quantity : 1,
                                unit_price: li.unit_price != null ? li.unit_price : 0,
                                tax_amount: li.tax_amount != null ? li.tax_amount : 0,
                            })));
                        }
                    }
                })
                .catch(err => {
                    setError(err.response?.data?.message || 'Failed to load bill');
                })
                .finally(() => setLoading(false));
        }
    }, [isEdit, billId]);

    // When vendor is selected, auto-fill payment terms
    useEffect(() => {
        if (selectedVendor && selectedVendor.payment_terms && !isEdit) {
            setPaymentTerms(selectedVendor.payment_terms);
            setDueDateManual(false);
        }
    }, [selectedVendor, isEdit]);

    const updateLine = (index, field, value) => {
        const updated = [...lines];
        updated[index] = { ...updated[index], [field]: value };
        setLines(updated);
    };

    const updateLineGlAccount = (index, account) => {
        const updated = [...lines];
        updated[index] = { ...updated[index], gl_account_id: account?.id || null, gl_account: account };
        setLines(updated);
    };

    const addLine = () => {
        setLines([...lines, { ...emptyLine }]);
    };

    const removeLine = (index) => {
        if (lines.length > 1) {
            setLines(lines.filter((_, i) => i !== index));
        }
    };

    // Calculations
    const lineAmount = (line) => {
        return (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
    };

    const subtotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);
    const totalTax = lines.reduce((sum, l) => sum + (parseFloat(l.tax_amount) || 0), 0);
    const total = subtotal + totalTax;

    const buildPayload = () => {
        return {
            vendor_id: selectedVendor?.id || null,
            vendor_invoice_number: vendorInvoiceNumber || null,
            bill_date: billDate,
            due_date: dueDate,
            payment_terms: paymentTerms,
            memo: memo || null,
            reference_number: referenceNumber || null,
            lines: lines.map(l => ({
                id: l.id || undefined,
                description: l.description,
                gl_account_id: l.gl_account_id || null,
                quantity: parseFloat(l.quantity) || 0,
                unit_price: parseFloat(l.unit_price) || 0,
                tax_amount: parseFloat(l.tax_amount) || 0,
            })),
        };
    };

    const handleSaveDraft = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = buildPayload();
            payload.status = 'DRAFT';

            if (isEdit) {
                await axiosInstance.put(`/api/finance/bills/${billId}`, payload);
                setSuccess('Bill updated successfully');
            } else {
                await axiosInstance.post('/api/finance/bills', payload);
            }
            navigate('/finance-portal/bills');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save bill');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmitForApproval = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = buildPayload();
            payload.status = 'PENDING_APPROVAL';

            if (isEdit) {
                await axiosInstance.put(`/api/finance/bills/${billId}`, payload);
            } else {
                await axiosInstance.post('/api/finance/bills', payload);
            }
            navigate('/finance-portal/bills');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit bill for approval');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/bills')} sx={{ color: 'white', mr: 0.5 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DescriptionIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                            {isEdit ? `Edit Bill ${existingBill?.bill_number || ''}` : 'New Bill'}
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            {isEdit ? 'Update bill details' : 'Create a new vendor bill'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Bill Details */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Bill Details</Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Autocomplete
                                size="small"
                                options={vendorOptions}
                                getOptionLabel={opt => opt ? (opt.company_name || `${opt.first_name || ''} ${opt.last_name || ''}`.trim()) : ''}
                                value={selectedVendor}
                                onChange={(_, val) => setSelectedVendor(val)}
                                onInputChange={(_, val) => setVendorSearch(val)}
                                loading={vendorLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Vendor"
                                        required
                                        InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { fontFamily, borderRadius: '10px' },
                                            endAdornment: (
                                                <>
                                                    {vendorLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Type to search vendors"
                            />
                            <FormControl size="small">
                                <InputLabel sx={{ fontFamily }}>Payment Terms</InputLabel>
                                <Select
                                    value={paymentTerms}
                                    onChange={e => { setPaymentTerms(e.target.value); setDueDateManual(false); }}
                                    label="Payment Terms"
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {PAYMENT_TERMS.map(pt => (
                                        <MenuItem key={pt.value} value={pt.value} sx={{ fontFamily }}>{pt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                            <TextField
                                size="small" label="Vendor Invoice #"
                                value={vendorInvoiceNumber}
                                onChange={e => setVendorInvoiceNumber(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" type="date" label="Bill Date" required
                                value={billDate}
                                onChange={e => { setBillDate(e.target.value); setDueDateManual(false); }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" type="date" label="Due Date" required
                                value={dueDate}
                                onChange={e => { setDueDate(e.target.value); setDueDateManual(true); }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" label="Reference #"
                                value={referenceNumber}
                                onChange={e => setReferenceNumber(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Line Items */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Line Items</Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addLine}
                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                            >
                                Add Line Item
                            </Button>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Description</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 220 }}>GL Account</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 80 }}>Qty</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Unit Price</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Amount</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 100 }}>Tax</TableCell>
                                        <TableCell sx={{ width: 50 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lines.map((line, index) => {
                                        const amt = lineAmount(line);
                                        return (
                                            <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        maxRows={3}
                                                        placeholder="Description"
                                                        value={line.description}
                                                        onChange={e => updateLine(index, 'description', e.target.value)}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <Autocomplete
                                                        size="small"
                                                        options={glAccounts}
                                                        getOptionLabel={opt => opt ? `${opt.account_number || ''} - ${opt.name || ''}`.trim() : ''}
                                                        value={line.gl_account}
                                                        onChange={(_, val) => updateLineGlAccount(index, val)}
                                                        loading={glLoading}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Account"
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    sx: { fontFamily, fontSize: '0.8rem', borderRadius: '8px' },
                                                                    endAdornment: (
                                                                        <>
                                                                            {glLoading ? <CircularProgress color="inherit" size={14} /> : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        noOptionsText="No accounts"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="1"
                                                        value={line.quantity}
                                                        onChange={e => updateLine(index, 'quantity', e.target.value)}
                                                        inputProps={{ min: 0, step: 'any' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={line.unit_price}
                                                        onChange={e => updateLine(index, 'unit_price', e.target.value)}
                                                        inputProps={{ min: 0, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ verticalAlign: 'top', pt: 1.5 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                        {formatCurrency(amt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={line.tax_amount}
                                                        onChange={e => updateLine(index, 'tax_amount', e.target.value)}
                                                        inputProps={{ min: 0, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeLine(index)}
                                                        disabled={lines.length <= 1}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addLine}
                            sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1.5, fontWeight: 600 }}
                        >
                            Add Line Item
                        </Button>
                    </CardContent>
                </Card>

                {/* Memo & Totals */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                    {/* Memo */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Memo</Typography>
                            <TextField
                                label="Memo / Notes"
                                size="small"
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Internal memo for this bill"
                                value={memo}
                                onChange={e => setMemo(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </CardContent>
                    </Card>

                    {/* Totals */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Summary</Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Subtotal</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(subtotal)}</Typography>
                                </Box>
                                {totalTax > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Tax</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(totalTax)}</Typography>
                                    </Box>
                                )}
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>Total</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(total)}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                        onClick={() => navigate('/finance-portal/bills')}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveDraft}
                        disabled={saving || !selectedVendor}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            borderColor: ACCENT, color: ACCENT,
                            '&:hover': { borderColor: '#16a34a', bgcolor: 'rgba(34,197,94,0.04)' },
                        }}
                    >
                        Save as Draft
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        onClick={handleSubmitForApproval}
                        disabled={saving || !selectedVendor}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                        }}
                    >
                        Submit for Approval
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
